import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { getLocale } from "react-i18nify";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";

import * as MatStyled from "@mui/material/styles";
import { Avatar, Grid } from "@mui/material";

import { getDetailUser } from "src/api/user";

import { useAuth } from "src/hooks/useAuth";
import Forbidden from "src/pages/Forbidden";
import { decryptData } from "src/components/profile/DecryptData";

const Container = styled.div`
  width: 100%;
  max-width: 1240px;
  position: relative;
  padding: 80px 120px;
  margin: auto;
  & > div > p {
    word-wrap: break-word;
  }
`;

const DivBackground = styled.div`
  width: 100%;
  padding: 32px;
  background-color: #f5f5f5;
  box-shadow: rgb(0 0 0 / 16%) 3px 4px 3px;
  border-radius: 30px;
  margin: 16px 0;
`;

const SpanTitle = styled.span`
  color: #000;
  font-size: 1.5rem;
  font-weight: 500;
  margin-bottom: 16px;
`;

const SpanHeader = styled.p`
  color: #000;
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 16px;
`;

const SpanSubHeader = styled.p`
  color: rgb(106 106 109);
  font-size: 0.75rem;
  font-weight: 400;
  margin-bottom: 16px;
`;

const SpanContent = styled.p`
  color: #000;
  font-size: 1rem;
  font-weight: 400;
  margin-bottom: 16px;
`;

const SpanContentDesc = styled.p`
  color: #000;
  font-size: 1rem;
  font-weight: 400;
  margin-bottom: 16px;
  min-height: 90px;
  max-height: 150px;
  overflow-y: scroll;
  ::-webkit-scrollbar {
    width: 7px;
    height: 7px;
  }

  /* Track */
  ::-webkit-scrollbar-track {
    background: transparent;
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: #b9b9b9;
    border-radius: 100px;
  }

  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: #b9b9b9;
  }
  ::-webkit-scrollbar-corner {
    background: transparent;
  }
`;
const drawerWidth = 260;

const Main = MatStyled.styled("main", {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  position: "relative",
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
    position: "relative",
  }),
}));

const UserDetail = ({ open }) => {
  const { user } = useAuth();
  let { userId } = useParams();
  const navigate = useNavigate();

  const [userDataDetail, setSellerData] = useState({});

  useEffect(() => {
    if (userId === undefined) return;
    (async () => {
      await getDetailUser(userId)
        .then(async ({ data }) => {
          const decrypt = await decryptData(data);
          setSellerData(decrypt.user);
        })
        .catch((err) => {
          navigate("/404");
        });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  if (user?.role_id === 3) return <Forbidden />;

  return (
    <Main
      sx={{ width: "100%", marginTop: "75px", background: "#fff" }}
      open={open}
    >
      <Container>
        <SpanTitle>
          {getLocale() === "th" ? "รายละเอียดผู้ใช้งาน" : "User detail"}
        </SpanTitle>
        <Grid container spacing={4}>
          <Grid item xs={12} sx={{ margin: "0 auto" }}>
            <DivBackground>
              <SpanHeader>
                {getLocale() === "th" ? "บัญชีโปรไฟล์" : "Profile account"}
              </SpanHeader>
              <Avatar
                src={userDataDetail?.user_profile_image}
                backgroundColor="rgba(0,0,0,0)"
                style={{
                  border: 0,
                  marginBottom: "24px",
                  width: "120px",
                  height: "120px",
                }}
              />
              <SpanSubHeader>
                {getLocale() === "th" ? "ชื่อ-นามสกุล" : "Full name"}
              </SpanSubHeader>
              <SpanContent>
                {userDataDetail?.register_type === 1
                  ? userDataDetail?.first_name + " " + userDataDetail?.last_name
                  : userDataDetail?.register_type === 2
                    ? userDataDetail?.facebook_name
                    : userDataDetail?.register_type === 3
                      ? userDataDetail?.google_name
                      : "-"}
              </SpanContent>
              <div>
                <SpanSubHeader>
                  {getLocale() === "th" ? "วันที่เข้าร่วม" : "Joined Date"}
                </SpanSubHeader>
                <SpanContent>
                  {userDataDetail?.createdAt === "" ||
                  userDataDetail?.createdAt === "null" ||
                  userDataDetail?.createdAt === null ||
                  userDataDetail?.createdAt === undefined
                    ? "-"
                    : moment(userDataDetail?.createdAt).format("L")}
                </SpanContent>
                <SpanSubHeader>
                  {getLocale() === "th" ? "เข้าร่วมผ่าน" : "Joined By"}
                </SpanSubHeader>
                <SpanContent>
                  {userDataDetail?.register_type === 1
                    ? getLocale() === "th"
                      ? "Xsurface"
                      : "Xsurface"
                    : userDataDetail?.register_type === 2
                      ? getLocale() === "th"
                        ? "Facebook"
                        : "Facebook"
                      : userDataDetail?.register_type === 3
                        ? getLocale() === "th"
                          ? "Google"
                          : "Google"
                        : "-"}
                </SpanContent>
                <SpanSubHeader>
                  {getLocale() === "th" ? "เบอร์โทรศัพท์" : "Phone number"}
                </SpanSubHeader>
                <SpanContent>
                  {userDataDetail?.phone_number === "" ||
                  userDataDetail?.phone_number === "null" ||
                  userDataDetail?.phone_number === null ||
                  userDataDetail?.phone_number === undefined
                    ? "-"
                    : userDataDetail?.phone_number}
                </SpanContent>
                <SpanSubHeader>
                  {getLocale() === "th" ? "อีเมลของคุณ" : "Your email address"}
                </SpanSubHeader>
                <SpanContent>
                  {userDataDetail?.email === "" ||
                  userDataDetail?.email === "null" ||
                  userDataDetail?.email === null ||
                  userDataDetail?.email === undefined
                    ? "-"
                    : userDataDetail?.email}
                </SpanContent>
              </div>
              <SpanSubHeader>
                {getLocale() === "th" ? "ที่อยู่" : "Address"}
              </SpanSubHeader>
              <SpanContent>
                {userDataDetail?.address === "" ||
                userDataDetail?.address === "null" ||
                userDataDetail?.address === null ||
                userDataDetail?.address === undefined
                  ? "-"
                  : userDataDetail?.address}
              </SpanContent>
              <SpanSubHeader>
                {getLocale() === "th" ? "อาชีพ" : "Career"}
              </SpanSubHeader>
              <SpanContentDesc>
                {getLocale() === "en"
                  ? userDataDetail?.career?.career_en
                    ? userDataDetail?.career?.career_en
                    : "-"
                  : userDataDetail?.career?.career_th
                    ? userDataDetail?.career?.career_th
                    : "-"}
              </SpanContentDesc>
            </DivBackground>
          </Grid>
        </Grid>
      </Container>
    </Main>
  );
};
export default UserDetail;
